import { attr, belongsTo } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validateInclusion,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import type { Summary } from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import type Performer from "parog-web/models/performer";
import tWithDefault from "parog-web/utils/t-with-default";
import JournalEvent from "./refunded-journal-event";

//---- Automatically generated, don't update this part ----

type RiskStringEnum =
  | "MALADIE"
  | "MATERNITE"
  | "ACCIDENT"
  | "TRAVAIL"
  | "PREVENTION";

type ExemptionStringEnum = "ALD";

type VisitSourceConceptStringEnum =
  | "GHSM"
  | "GHSC"
  | "GHSO"
  | "DMT"
  | "GHS"
  | "DDP"
  | "NGAP";

/**
 * Describe "CP" (hôpitaux, établissements) reimbursement.
 * Property descriptions are sometimes copy/pasted from the DMP specification.
 *
 * @see sel-mp-005_hr-integre-lps_v-1.9.pdf for details.
 *
 * The DMP feed provides multiple visit occurrences for each Execution.
 * One hospitalisation-refunded is created for each occurrence.
 *
 * This JE tries to be as close as possible to the OMOP VISIT_OCCURRENCE.
 */
export default class HospitalisationRefundedJournalEvent extends JournalEvent {
  static override type = "hospitalisation-refunded-journal-event";

  /**
   * Nature du risque au titre duquel la prestation est prise en charge.
   * Valeurs possibles : «MALADIE», «MATERNITE», «ACCIDENT», «TRAVAIL»,
   * «PREVENTION»
   */
  @attr("string") declare risk: RiskStringEnum;

  /**
   * Valeur = «ALD» si présente
   */
  @attr("string") declare exemption: ExemptionStringEnum;

  /**
   * Date d’admission du séjour
   */
  @attr("date") declare visitStartDate: Date;

  /**
   * Date de fin du séjour
   */
  @attr("date") declare visitEndDate: Date;

  /**
   * Deux types de code d’hospitalisation sont possibles selon les séjours et un dernier rajouté pour le DMP:
   * - «GHS» (Groupe Homogène de Séjour) pour les établissement appliquant la T2A
   *   - «GHSM» (Médecine),
   *   - «GHSC» (Chirurgie),
   *   - «GHSO» (Obstétrique).
   * - «DMT» (Discipline Médico Tarifaire) pour les établissement ne l’appliquant pas.
   * - «GHS», «DDP» et «NGAP» sont des codes fournis par le DMP.
   */
  @attr("string") declare visitSourceConcept: VisitSourceConceptStringEnum;

  /**
   * Le code correspond à la nature de l’hospitalisation :
   * - «2727» (pour le type GHS),
   * - «156» (pour le type DMT).
   */
  @attr("string") declare visitSourceValue: string;

  /**
   * Contient le libellé de la nature de l'hospitalisation : «Arthroscopies»
   * (pour le type GHS), «O.R.L» (pour le type DMT).
   */
  @attr("string") declare visitSourceLabel: string;

  @attr("performer") declare performer: Performer;

  static override validations(intl: IntlService) {
    return {
      reimbursementsRetrievedJournalEvent: validatePresence({
        presence: true,
        description: intl.t(
          "validations.hospitalisation-refunded-journal-event.reimbursements-retrieved-journal-event"
        )
      }),
      visitStartDate: validatePresence({
        presence: true,
        description: intl.t(
          "validations.hospitalisation-refunded-journal-event.visit-start-date"
        )
      }),
      visitSourceConcept: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.hospitalisation-refunded-journal-event.visit-source-concept"
          )
        }),
        validateInclusion({
          list: ["GHSM", "GHSC", "GHSO", "DMT", "GHS", "DDP", "NGAP"],
          description: intl.t(
            "validations.hospitalisation-refunded-journal-event.visit-source-concept"
          )
        })
      ],
      visitSourceLabel: validatePresence({
        presence: true,
        description: intl.t(
          "validations.hospitalisation-refunded-journal-event.visit-source-label"
        )
      }),
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.hospitalisation-refunded-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ],
      risk: validateInclusion({
        list: ["MALADIE", "MATERNITE", "ACCIDENT", "TRAVAIL", "PREVENTION"],
        allowBlank: true,
        description: intl.t(
          "validations.hospitalisation-refunded-journal-event.risk"
        )
      }),
      exemption: validateInclusion({
        list: ["ALD"],
        allowBlank: true,
        description: intl.t(
          "validations.hospitalisation-refunded-journal-event.exemption"
        )
      })
    };
  }

  //---- End of generated code ----

  @belongsTo("patient", {
    async: false,
    inverse: "hospitalisationRefundedJournalEvents"
  })
  declare patient: Patient;

  override get description() {
    return `${super.description} ${this.visitSourceLabel}`;
  }

  override get summary() {
    const result: Summary & { details?: string } = super.summary;
    result.details = this.intl.t(
      `timeline.journal-event.${HospitalisationRefundedJournalEvent.type}.details`,
      {
        visitStartDate: this.intl.formatDate(this.visitStartDate),
        visitEndDate: this.intl.formatDate(this.visitEndDate)
      }
    );
    return result;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "hospitalisation-refunded-journal-event": HospitalisationRefundedJournalEvent;
  }
}
